import type { DataProvider } from "react-admin";
import { callApi } from "../../../../dataprovider/misc-apis";

export type ItsdataFamilyMember = {
  HOF_FM_TYPE: string;
  Full_Name: string;
  Age: string;
  Gender: string;
  ITS_ID: string;
};

export type ItsdataHofRecord = {
  id: string | number;
  ITS_ID: string;
  Full_Name: string;
  Address?: string;
  Mobile?: string;
  HOF_ID?: string;
  HOF_FM_TYPE?: string;
  familyMembers?: ItsdataFamilyMember[];
};

type ItsdataLookupRow = {
  ITS_ID: string;
  HOF_ID?: string;
  HOF_FM_TYPE?: string;
};

export function getHofItsId(row: ItsdataLookupRow): string {
  return row.HOF_FM_TYPE === "HOF" ? row.ITS_ID : (row.HOF_ID ?? row.ITS_ID);
}

export async function fetchHofWithFamily(
  dataProvider: DataProvider,
  hofItsId: string
): Promise<ItsdataHofRecord | null> {
  const { data } = await dataProvider.getList("itsdata", {
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
    filter: {
      ITS_ID: hofItsId,
      isHOF: true,
      includeFamily: true,
    },
  });

  return (data[0] as ItsdataHofRecord | undefined) ?? null;
}

export async function resolveHofLookupResults(
  dataProvider: DataProvider,
  rows: ItsdataLookupRow[],
  searchedIts: string
): Promise<ItsdataHofRecord[]> {
  const trimmed = searchedIts.trim();
  const exactMatch = trimmed ? rows.find((row) => row.ITS_ID === trimmed) : undefined;
  const candidates = exactMatch ? [exactMatch] : rows;

  const hofIds = [
    ...new Set(candidates.map(getHofItsId).filter((hofId): hofId is string => Boolean(hofId))),
  ];

  const hofs = await Promise.all(hofIds.map((hofId) => fetchHofWithFamily(dataProvider, hofId)));
  return hofs.filter((hof): hof is ItsdataHofRecord => hof != null);
}

export function buildItsLookupFilter(itsNo: string): Record<string, unknown> {
  const trimmed = itsNo.trim();
  if (/^\d+$/.test(trimmed)) {
    return { ITS_ID: trimmed, includeFamily: true };
  }
  return { q: trimmed, includeFamily: true };
}

export type NiyaazPreviousHistory = { rows?: unknown[] };

function isPreviousHistoryPayload(data: unknown): data is NiyaazPreviousHistory {
  return typeof data === "object" && data !== null && "rows" in data;
}

export async function fetchNiyaazPreviousHistory(
  hofItsId: string
): Promise<NiyaazPreviousHistory | null> {
  const { data } = await callApi({
    location: "niyaaz",
    data: {
      HOFId: hofItsId,
      includeEventData: true,
    },
    method: "GET",
  });

  return isPreviousHistoryPayload(data) ? data : null;
}
